// The event model. Pure: types, constructors, and one parser for rows this
// build did not write. No React, no DOM, no SDK.

import type { LessonId, ProgressKey } from "../content/types.ts";
import { isLangId } from "../lang.ts";
import { isVersion } from "../onboarding/version.ts";
import { uid } from "../rng.ts";
import type { GameState } from "../state.ts";
import { isLayoutId } from "../writing/layouts.ts";
import {
	makeClock,
	stampWall,
	type Clock,
	type DeviceId,
	type EventId,
	type Stamp,
} from "./clock.ts";
import { isTreeName } from "./tree-name.ts";

/**
 * Which rule graded a step.
 *
 * A fact about the session, not a derived value: `check` is a graded step's
 * single verdict, `score` is a self-scored step (match) reporting one key at a
 * time. They carry different penalties (reduce.ts's DELTA) and the event cannot
 * say which applied without this, so a two-field Outcome could not reproduce
 * today's scoring. The deltas themselves stay in the reducer — events never
 * carry a number the rules could later change.
 */
export type Grading = "check" | "score";

export interface Outcome {
	key: ProgressKey;
	right: boolean;
	via: Grading;
	/**
	 * On a wrong single pick, the item id of the option chosen. Never typed
	 * text, and never read by the fold: it is for the learner's own history, so
	 * `FOLD_RULES` is unchanged and an older build reads the event as before.
	 */
	picked?: string;
}

/**
 * What a finished lesson reports. `mistakes`, `firstTry` and `graded` are NOT
 * derivations of `outcomes` — `graded` counts only the step types that count
 * toward accuracy, and `firstTry` distinguishes a first attempt from a retry.
 * Neither is recoverable from an outcome list, so both stay on the event.
 */
export interface LessonResult {
	outcomes: Outcome[];
	/** Total wrong answers, including repeats of the same step. */
	mistakes: number;
	firstTry: number;
	/** Steps that counted toward accuracy. */
	graded: number;
	/**
	 * When the lesson began (epoch ms) and its active time in ms. Like
	 * `Outcome.picked`, never read by the fold: for the learner's own history and
	 * analytics, so an older build reads the event as before.
	 */
	startedAt?: number;
	activeMs?: number;
	/**
	 * Rounds played with listening off: swapped for a silent alternative, dropped
	 * from the lesson, or skipped. Like `picked`, never read by the fold — the
	 * learner's own history, so `FOLD_RULES` is unchanged and an older build
	 * reads the event as before.
	 */
	muted?: { swapped: number; dropped: number; skipped: number };
}

/**
 * The GameState fields the fold owns — everything an event log computes.
 *
 * Written out so that PrefKey can be its complement, which is what will force
 * a new GameState field to be *classified* rather than merely noticed: it
 * lands in PrefKey, and reduce.ts's PREF mapped type is then missing a member.
 *
 * One caveat, stated because this is exactly the kind of comment this
 * codebase has been wrong about fourteen times. A new field already fails
 * `tsc` today for a duller reason — storage.ts's defaultState() returns a
 * GameState literal — so what PREF adds is the decision about behaviour, not
 * the alarm that something changed.
 */
type FoldedKey = "stats" | "done" | "unlocked" | "slips";

/** The device preferences, which are the one thing a fold cannot derive. */
export type PrefKey = Exclude<keyof GameState, FoldedKey>;

export type PrefPatch = Partial<Pick<GameState, PrefKey>>;

export type EventBody =
	| ({ kind: "lesson"; lessonId: LessonId | null } & LessonResult)
	| { kind: "pref"; patch: PrefPatch };

/**
 * One fact, recorded once, never edited.
 *
 * There is no `reset` variant and there will not be one until something
 * emits it. `id` is also the Appwrite row id, which is what makes a retried
 * push a 409 rather than a duplicate.
 */
export type ProgressEvent = { id: EventId; at: Stamp; dev: DeviceId } & EventBody;

/** Everything minting an event needs, held once per device rather than per call. */
export interface EventContext {
	dev: DeviceId;
	clock: Clock;
	newId: () => EventId;
}

export const makeContext = (
	dev: DeviceId,
	clock: Clock = makeClock(),
	newId: () => EventId = uid,
): EventContext => ({ dev, clock, newId });

// Generic over the caller's own EventBody branch, so `makeEvent({kind:
// "lesson", ...}, ctx)` comes back typed as a lesson event rather than
// widened to ProgressEvent — no cast needed at any of its three call sites.
// The intersection is still assignable to ProgressEvent, so nothing that
// consumes a plain ProgressEvent has to change.
export const makeEvent = <B extends EventBody>(
	body: B,
	ctx: EventContext,
): { id: EventId; at: Stamp; dev: DeviceId } & B => ({
	id: ctx.newId(),
	at: ctx.clock.next(),
	dev: ctx.dev,
	...body,
});

// --- the trust boundary -----------------------------------------------------

const isObj = (x: unknown): x is Record<string, unknown> =>
	typeof x === "object" && x !== null && !Array.isArray(x);

const isStr = (x: unknown): x is string => typeof x === "string";
const isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

const isCount = (x: unknown): x is number => isNum(x) && Number.isInteger(x) && x >= 0;

const GRADINGS: readonly Grading[] = ["check", "score"];

function parseOutcome(x: unknown): Outcome | null {
	if (!isObj(x)) return null;
	if (!isStr(x.key) || typeof x.right !== "boolean") return null;
	// GRADINGS lists exactly Grading's members, so a match narrows soundly.
	if (!isStr(x.via) || !(GRADINGS as readonly string[]).includes(x.via)) return null;
	return {
		key: x.key,
		right: x.right,
		via: x.via as Grading,
		...(isStr(x.picked) ? { picked: x.picked } : {}),
	};
}

/**
 * Keeps only the preference keys this build knows, and only with the right
 * type. A newer build's fourth preference is dropped rather than carried into
 * GameState as an unknown field — the event is still worth folding for the
 * keys we do understand.
 */
function parsePatch(x: unknown): PrefPatch | null {
	if (!isObj(x)) return null;
	const out: PrefPatch = {};
	// A retired build's `welcomed` flag falls out here like any unknown key.
	if (isVersion(x.onboarded)) out.onboarded = x.onboarded;
	// isLangId is lang.ts's ready-made validator for exactly this boundary; it
	// is the same guard storage.ts applies to a stored `lang`.
	if (isStr(x.lang) && isLangId(x.lang)) out.lang = x.lang;
	if (isLayoutId(x.layout)) out.layout = x.layout;
	if (typeof x.kbdHinted === "boolean") out.kbdHinted = x.kbdHinted;
	if (isTreeName(x.treeName)) out.treeName = x.treeName;
	return out;
}

// Each count stands alone like startedAt and activeMs: all three must be
// counts, or the block is dropped and the rest of the event is kept.
function parseMuted(x: unknown): LessonResult["muted"] | null {
	if (!isObj(x)) return null;
	const { swapped, dropped, skipped } = x;
	if (!isCount(swapped) || !isCount(dropped) || !isCount(skipped)) return null;
	return { swapped, dropped, skipped };
}

/**
 * A row this build did not write, turned into an event or refused.
 *
 * Nullable rather than throwing: "skip what you cannot interpret" is
 * the fold's contract, and this is where that decision is actually taken.
 */
export function parseEvent(raw: unknown): ProgressEvent | null {
	if (!isObj(raw)) return null;
	const { id, at, dev, kind } = raw;
	// `at` is checked only for being a string, not for the stamp format
	// (clock.ts's STAMP pattern). That second check is reduce.ts's job: an
	// event with an unusable `at` parses fine here and is dropped later by
	// `stampWall(e.at) < 0`, the same gate a stamp corrupted after parsing
	// (e.g. in localStorage) would also have to pass.
	if (!isStr(id) || !isStr(at) || !isStr(dev)) return null;

	if (kind === "pref") {
		const patch = parsePatch(raw.patch);
		return patch === null ? null : { id, at, dev, kind, patch };
	}

	if (kind === "lesson") {
		const { lessonId, outcomes, mistakes, firstTry, graded } = raw;
		if (lessonId !== null && !isStr(lessonId)) return null;
		if (!Array.isArray(outcomes)) return null;
		if (!isNum(mistakes) || !isNum(firstTry) || !isNum(graded)) return null;
		const parsed: Outcome[] = [];
		for (const o of outcomes) {
			const one = parseOutcome(o);
			// One bad outcome makes the whole event uninterpretable: a partial
			// outcome list would silently change the learner's strength.
			if (!one) return null;
			parsed.push(one);
		}
		// A start after the event's own wall time cannot be true; each field stands alone.
		const { startedAt, activeMs } = raw;
		const muted = parseMuted(raw.muted);
		return {
			id,
			at,
			dev,
			kind,
			lessonId,
			outcomes: parsed,
			mistakes,
			firstTry,
			graded,
			...(isCount(startedAt) && startedAt <= stampWall(at) ? { startedAt } : {}),
			...(isCount(activeMs) ? { activeMs } : {}),
			...(muted ? { muted } : {}),
		};
	}

	// An unknown kind is a newer build's event. Skipped, never guessed at.
	return null;
}
